import { useState } from 'react'
import { StaticPageLayout } from '../layouts/StaticPageLayout'
import { useAuth } from '../hooks/auth/useAuth'
import { SettingsSection, SettingItem, Toggle } from '../components/settings'
import { ActionButton, ContentSection, H3, Version } from '../components/ui'
import { SEO } from '../components/SEO/SEO'

export const Settings = () => {
  const { user, isAuthenticated } = useAuth()

  const seoConfig = {
    title: 'Settings - Et Ai Account Preferences',
    description:
      'Manage your Et Ai account settings, preferences, and privacy options. Customize your AI research assistant experience.',
    keywords: 'Et Ai settings, account preferences, privacy settings, AI tool configuration',
    url: 'https://etai.app/settings',
    canonical: 'https://etai.app/settings',
    noindex: true, // Settings pages typically shouldn't be indexed
    nofollow: true,
  }

  const [settings, setSettings] = useState({
    darkMode: true, // Currently always dark, prepare for future light mode
    emailNotifications: true,
    serviceImprovement: true,
    analyticsOptIn: false,
    autoSave: true,
    soundEnabled: false,
  })

  const updateSetting = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
    // TODO: Save to backend when user settings API is implemented
  }

  const handleDeleteAccount = () => {
    if (
      window.confirm('Are you sure you want to delete your account? This action cannot be undone.')
    ) {
      // TODO: Implement account deletion
    }
  }

  const handleExportData = () => {
    // TODO: Implement data export
  }

  return (
    <StaticPageLayout title="Settings">
      <SEO {...seoConfig} />
      <div className="space-y-8">
        <p className="text-gray-400 text-center mb-8">
          Customize your Et Ai Labs experience and manage your account preferences.
        </p>

        {/* Account Settings */}
        {isAuthenticated && (
          <SettingsSection title="Account">
            <SettingItem
              label="Profile Information"
              description={`Signed in as ${user?.email || 'Unknown'}`}
            >
              <ActionButton variant="secondary" size="sm">
                Edit Profile
              </ActionButton>
            </SettingItem>
            <SettingItem
              label="Export Data"
              description="Download a copy of your data and conversation history"
            >
              <ActionButton variant="primary" size="sm" onClick={handleExportData}>
                Export
              </ActionButton>
            </SettingItem>
            <SettingItem
              label="Delete Account"
              description="Permanently delete your account and all associated data"
            >
              <ActionButton variant="danger" size="sm" onClick={handleDeleteAccount}>
                Delete
              </ActionButton>
            </SettingItem>
          </SettingsSection>
        )}

        {/* Authentication Notice for Guest Users */}
        {!isAuthenticated && (
          <SettingsSection title="Account">
            <div className="text-center py-8">
              <H3 className="mb-2">Sign in to access account settings</H3>
              <p className="text-gray-400 mb-6">
                Create an account or sign in to customize your preferences and save your settings.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <ActionButton href="/login" variant="primary" size="md">
                  Sign In
                </ActionButton>
                <ActionButton href="/register" variant="secondary" size="md">
                  Create Account
                </ActionButton>
              </div>
            </div>
          </SettingsSection>
        )}

        {/* Appearance Settings */}
        <SettingsSection title="Appearance">
          <SettingItem label="Dark Mode" description="Use dark theme (light mode coming soon)">
            <Toggle
              enabled={settings.darkMode}
              onChange={(value) => updateSetting('darkMode', value)}
              disabled={true} // Disabled until light mode is implemented
            />
          </SettingItem>
          <SettingItem label="Sound Effects" description="Enable audio feedback for interactions">
            <Toggle
              enabled={settings.soundEnabled}
              onChange={(value) => updateSetting('soundEnabled', value)}
            />
          </SettingItem>
        </SettingsSection>

        {/* Privacy Settings */}
        <SettingsSection title="Privacy & Data">
          <SettingItem
            label="Service Improvement"
            description="Help improve our AI by allowing anonymized analysis of your queries"
          >
            <Toggle
              enabled={settings.serviceImprovement}
              onChange={(value) => updateSetting('serviceImprovement', value)}
            />
          </SettingItem>
          <SettingItem
            label="Analytics"
            description="Allow collection of usage analytics to improve the platform"
          >
            <Toggle
              enabled={settings.analyticsOptIn}
              onChange={(value) => updateSetting('analyticsOptIn', value)}
            />
          </SettingItem>
        </SettingsSection>

        {/* Notification Settings */}
        <SettingsSection title="Notifications">
          <SettingItem
            label="Email Notifications"
            description="Receive important updates and announcements via email"
          >
            <Toggle
              enabled={settings.emailNotifications}
              onChange={(value) => updateSetting('emailNotifications', value)}
            />
          </SettingItem>
        </SettingsSection>

        {/* Chat Settings */}
        <SettingsSection title="Chat & Interface">
          <SettingItem
            label="Auto-save Conversations"
            description="Automatically save your chat conversations"
          >
            <Toggle
              enabled={settings.autoSave}
              onChange={(value) => updateSetting('autoSave', value)}
            />
          </SettingItem>
        </SettingsSection>

        {/* Help & Support */}
        <SettingsSection title="Help & Support">
          <SettingItem label="Get Help" description="Access documentation and support resources">
            <ActionButton href="/help" variant="secondary" size="sm">
              Help Center
            </ActionButton>
          </SettingItem>
          <SettingItem label="Contact Support" description="Get in touch with our support team">
            <ActionButton href="/contact" variant="secondary" size="sm">
              Contact Us
            </ActionButton>
          </SettingItem>
          <SettingItem
            label="Privacy Policy"
            description="Review our data privacy and protection policies"
          >
            <ActionButton href="/privacy-policy" variant="secondary" size="sm">
              View Policy
            </ActionButton>
          </SettingItem>
        </SettingsSection>

        {/* Application Info */}
        <ContentSection title="Application Information" spacing="compact">
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div>
              <H3 className="mb-2">Version</H3>
              <p className="text-gray-400">
                <Version type="version" />
              </p>
            </div>
            <div>
              <H3 className="mb-2">Last Updated</H3>
              <p className="text-gray-400">
                <Version type="date" />
              </p>
            </div>
            <div>
              <H3 className="mb-2">Region</H3>
              <p className="text-gray-400">Global</p>
            </div>
          </div>
        </ContentSection>
      </div>
    </StaticPageLayout>
  )
}
